/**
 * D5/D6 · Student side of a take: playback with pitch overlay and
 * time-stamped comments (students can reply too).
 * D7/D8 · Status + coach reaction banner once reviewed.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useParams } from 'react-router';
import type { RenderedExercise, TracePoint } from '@solfo/shared';
import { api } from '../../lib/api.ts';
import { t, exName as exLabel, exDesc as exDescription } from '../../lib/i18n.ts';
import { Button, Card, ErrorText, Input, Spinner, Tag, fmtDateTime } from '../../lib/ui.tsx';
import PitchCanvas from '../../components/PitchCanvas.tsx';
import { CommentList, fmtClock, useTakePlayback } from '../coach/TakeReview.tsx';

interface TakeDetail {
  id: string;
  status: string;
  fileId: string;
  pitchTraceJson: string | null;
  refinedTraceJson: string | null;
  refinedScore: number | null;
  targetJson: string | null;
  accuracyScore: number | null;
  submittedAt: string | null;
  keepForever: boolean;
  reaction: string | null;
  syncOffsetSec: number | null;
  student?: { id: string; name: string };
  exercise?: { id: string; name: string };
  comments: {
    id: string;
    atSec: number;
    text: string | null;
    audioFileId: string | null;
    author: { id: string; name: string; role: string };
  }[];
}

const REACTION_BANNERS: Record<string, string> = {
  great: 'Great take ✓🎉',
  nice_progress: 'Nice progress 📈',
  keep_going: 'Keep going 💪',
};

export default function TakeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const [commentText, setCommentText] = useState('');
  const [error, setError] = useState<unknown>(null);

  const { data: take, isLoading } = useQuery({
    queryKey: ['take', id],
    queryFn: () => api<TakeDetail>(`/api/takes/${id}`),
  });
  const { audioUrl, audioRef, time, exerciseTime, rate, setRate } = useTakePlayback(take);

  const rendered = take?.targetJson ? (JSON.parse(take.targetJson) as RenderedExercise) : null;
  // prefer the server-refined trace (octave flips corrected) for the overlay
  const traceJson = take?.refinedTraceJson ?? take?.pitchTraceJson;
  const trace = traceJson ? (JSON.parse(traceJson) as TracePoint[]) : [];

  const comment = useMutation({
    mutationFn: (body: { atSec: number; text: string }) => api(`/api/takes/${id}/comments`, { body }),
    onSuccess: () => {
      setCommentText('');
      void qc.invalidateQueries({ queryKey: ['take', id] });
    },
    onError: setError,
  });

  if (isLoading) return <Spinner />;
  if (!take) return <ErrorText error="Take not found" />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold">{take.exercise ? exLabel(take.exercise) : t('take.title', 'Take')}</h1>
          <p className="text-xs text-slate-500">
            {take.submittedAt
              ? `${t('take.submitted', 'submitted')} ${fmtDateTime(take.submittedAt)}`
              : t('take.draft', 'draft — only you can see this')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {take.accuracyScore !== null && <Tag tone="brand">score {take.accuracyScore}</Tag>}
          {take.status === 'draft' && <Tag>draft</Tag>}
          {take.status === 'submitted' && <Tag tone="amber">{t('take.awaiting', 'awaiting review')}</Tag>}
          {take.status === 'reviewed' && <Tag tone="green">{t('take.reviewed', 'reviewed')}</Tag>}
          {take.status === 'archived' && <Tag>{t('take.archived', 'archived')}</Tag>}
        </div>
      </div>

      {/* D7/D8 · reaction banner */}
      {take.status === 'reviewed' && (
        <Card className="border-emerald-200 bg-emerald-50">
          <p className="text-sm font-medium text-emerald-900">
            {take.reaction && REACTION_BANNERS[take.reaction]
              ? `${t('take.coachSays', 'Your coach says:')} ${REACTION_BANNERS[take.reaction]}`
              : t('take.reviewedBanner', 'Your coach reviewed this take — check the comments below.')}
          </p>
        </Card>
      )}

      {rendered ? (
        <PitchCanvas rendered={rendered} trace={trace} currentTime={exerciseTime} mode="full" height={240} />
      ) : (
        <Card>
          <p className="text-xs text-slate-400">
            {t('take.noTrace', 'No pitch trace for this take (custom-audio exercise).')}
          </p>
        </Card>
      )}

      <Card>
        {audioUrl ? (
          <div className="flex items-center gap-3">
            <audio ref={audioRef} controls src={audioUrl} className="h-10 w-full" />
            <div className="flex shrink-0 gap-1">
              {[0.75, 1].map((r) => (
                <button
                  key={r}
                  onClick={() => setRate(r)}
                  className={`rounded-md px-2 py-1 text-xs font-medium ${rate === r ? 'bg-brand-600 text-white' : 'bg-slate-100 text-slate-600'}`}
                >
                  {r}×
                </button>
              ))}
            </div>
          </div>
        ) : (
          <Spinner label="Loading audio…" />
        )}
      </Card>

      <Card title={t('take.comments', 'Comments (pinned to the moment)')}>
        <CommentList
          comments={take.comments}
          onJump={(sec) => {
            if (audioRef.current) audioRef.current.currentTime = sec;
          }}
        />
        <div className="mt-3 flex gap-2">
          <span className="mt-2 shrink-0 font-mono text-xs text-slate-500">{fmtClock(time)}</span>
          <Input
            placeholder={t('take.reply', 'Reply at this moment…')}
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && commentText.trim()) {
                comment.mutate({ atSec: audioRef.current?.currentTime ?? 0, text: commentText.trim() });
              }
            }}
          />
          <Button
            variant="secondary"
            disabled={!commentText.trim() || comment.isPending}
            onClick={() => comment.mutate({ atSec: audioRef.current?.currentTime ?? 0, text: commentText.trim() })}
          >
            {t('take.pin', 'Pin')}
          </Button>
        </div>
      </Card>

      <ErrorText error={error} />
    </div>
  );
}
